// Change #100308 - Settings > Jobs > Tags.
//
// The company's Job Tags: "Active leak", "Sewage", "Vulnerable occupant". Put on
// a job by hand from the job header, or by a form answer (Form Builder field >
// "Apply a tag"). Highlight marks a tag as a warning, which is what the
// attending technician sees on My Day.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { FormControlLabel, Checkbox, TextField } from "@mui/material";
import { toast } from "react-toastify";

import { FetchAPI } from "../../../../../helpers/APIdata/fetchAPI";
import TableContainer from "../../../../../Components/Common/TableContainer";
import NoDataContent from "../../../../../Components/Common/NoDataContent";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import AppModal from "../../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../../Components/Common/Spinner";
import JobTagPill from "../../../../../Components/Common/JobTagPill/JobTagPill";
import IconPicker from "../../menus/components/IconPicker";

const EMPTY_TAG = {
  Name: "",
  Description: "",
  Icon: "ri-alert-line",
  CssColourBackground: "#FDE8E4",
  CssColourText: "#C0392B",
  CssColourBorder: "#F5B7A8",
  FlagHighlight: true,
  FlagActive: true,
  Order: "",
};

const SEARCH_FIELDS = ["Name", "Description", "Id"];

const isOn = (value) => value === true || String(value) === "1";

const JobTags = () => {
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modal, setModal] = useState(null); // null | { Id?, ...tag }
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const loadTags = useCallback(async () => {
    const response = await FetchAPI("/jobtag/list", "GET");
    if (response?.status === 200) {
      setTags(response.jobtag_list || []);
    } else {
      setTags([]);
      // 409 here is "database update pending" - worth saying once.
      if (response?.message) toast.info(response.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const visibleTags = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return tags;
    return tags.filter((row) =>
      SEARCH_FIELDS.some((key) =>
        String(row[key] || "").toLowerCase().includes(term)
      )
    );
  }, [tags, searchQuery]);

  const submitModal = async () => {
    if (!modal?.Name?.trim()) {
      toast.error("Give the tag a name");
      return;
    }
    setSaving(true);
    const fields = {
      Name: modal.Name.trim(),
      Description: modal.Description || null,
      Icon: modal.Icon || null,
      CssColourBackground: modal.CssColourBackground,
      CssColourText: modal.CssColourText,
      CssColourBorder: modal.CssColourBorder,
      FlagHighlight: isOn(modal.FlagHighlight) ? 1 : 0,
      FlagActive: isOn(modal.FlagActive) ? 1 : 0,
      Order: modal.Order === "" ? null : modal.Order,
    };
    const response = modal.Id
      ? await FetchAPI("/jobtag/update", "PUT", [{ Id: modal.Id, ...fields }])
      : await FetchAPI("/jobtag/create", "POST", [fields]);
    setSaving(false);
    if (response?.status === 200) {
      toast.success(modal.Id ? "Tag updated" : "Tag created");
      setModal(null);
      loadTags();
    } else {
      toast.error(response?.message || "Could not save the tag");
    }
  };

  const confirmDelete = async () => {
    const response = await FetchAPI("/jobtag/delete", "DELETE", [deleteId]);
    setDeleteId(null);
    if (response?.status === 200) {
      toast.success("Tag deleted");
      loadTags();
    } else {
      toast.error(response?.message || "Could not delete the tag");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Tag",
        accessor: "Name",
        filterable: false,
        Cell: (item) => {
          const row = item.row.original;
          return (
            <div className="text-wrap">
              <JobTagPill tag={row} />
              {row.Description ? (
                <div className="text-muted mt-1">{row.Description}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: "Highlight",
        accessor: "FlagHighlight",
        filterable: false,
        Cell: (item) =>
          isOn(item.row.original.FlagHighlight) ? (
            <span className="badge bg-danger-subtle text-danger">
              Warning, shown on My Day
            </span>
          ) : (
            <span className="text-muted">No</span>
          ),
      },
      {
        Header: "Active",
        accessor: "FlagActive",
        filterable: false,
        Cell: (item) =>
          isOn(item.row.original.FlagActive) ? "Yes" : <span className="text-muted">No</span>,
      },
      {
        Header: "Order",
        accessor: "Order",
        filterable: false,
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: (item) => (
          <UncontrolledDropdown>
            <DropdownToggle className="btn btn-soft-info btn-icon fs-14" type="button">
              <i className="fa-solid fa-ellipsis" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                onClick={() =>
                  setModal({
                    ...EMPTY_TAG,
                    ...item.row.original,
                    Description: item.row.original.Description || "",
                    Icon: item.row.original.Icon || "",
                    Order: item.row.original.Order ?? "",
                    FlagHighlight: isOn(item.row.original.FlagHighlight),
                    FlagActive: isOn(item.row.original.FlagActive),
                  })
                }
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                Edit
              </DropdownItem>
              <DropdownItem onClick={() => setDeleteId(item.row.original.Id)}>
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />
                Delete
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  const setField = (key) => (e) =>
    setModal((prev) => ({ ...prev, [key]: e.target.value }));
  const setFlag = (key) => (e) =>
    setModal((prev) => ({ ...prev, [key]: e.target.checked }));

  return (
    <div>
      <Row className="g-4 align-items-center mb-3">
        <Col sm={3}>
          <div className="search-box">
            <Input
              type="search"
              className="form-control search"
              placeholder="Search for..."
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
            <i className="ri-search-line search-icon"></i>
          </div>
        </Col>
        <div className="col-sm-auto ms-auto">
          <button
            type="button"
            className="btn btn-success add-btn"
            onClick={() => setModal({ ...EMPTY_TAG })}
          >
            <i className="ri-add-line align-bottom me-1"></i>
            Create Tag
          </button>
        </div>
      </Row>

      {loading ? (
        <LoadingSpinner />
      ) : visibleTags.length ? (
        <TableContainer
          columns={columns}
          data={visibleTags}
          isGlobalFilter={false}
          isAddUserList={false}
          customPageSize={10}
          className="custom-header-css"
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-wrap"
          theadClass="text-muted"
        />
      ) : (
        <NoDataContent
          titleText={searchQuery ? `No Results for: ${searchQuery}` : "No tags yet"}
          paragraphText={
            searchQuery
              ? `Try adjusting your query: ${searchQuery}`
              : "Create tags such as Active leak or Vulnerable occupant to flag jobs."
          }
          useButton={false}
        />
      )}

      <AppModal
        open={!!modal}
        onClose={() => !saving && setModal(null)}
        title={modal?.Id ? "Edit tag" : "New tag"}
        headerAccent="indigo"
        size="sm"
        loading={saving}
        submitLabel={modal?.Id ? "Save" : "Create"}
        submitDisabled={!modal?.Name?.trim()}
        onSubmit={submitModal}
      >
        <div className="d-grid gap-3">
          <div>
            <small className="text-muted d-block mb-1">Preview</small>
            <JobTagPill
              tag={{
                ...modal,
                Name: modal?.Name || "Tag name",
                FlagHighlight: isOn(modal?.FlagHighlight) ? 1 : 0,
              }}
            />
          </div>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name *"
            placeholder="e.g. Active leak"
            value={modal?.Name || ""}
            onChange={setField("Name")}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Description"
            value={modal?.Description || ""}
            onChange={setField("Description")}
          />
          <IconPicker
            value={modal?.Icon || ""}
            onChange={(icon) => setModal((prev) => ({ ...prev, Icon: icon }))}
          />
          <div className="d-flex gap-3 flex-wrap">
            {[
              ["CssColourBackground", "Background"],
              ["CssColourText", "Text"],
              ["CssColourBorder", "Border"],
            ].map(([key, label]) => (
              <label key={key} className="d-flex flex-column small text-muted">
                {label}
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={modal?.[key] || "#ffffff"}
                  onChange={setField(key)}
                />
              </label>
            ))}
          </div>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Order"
            value={modal?.Order ?? ""}
            onChange={setField("Order")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isOn(modal?.FlagHighlight)}
                onChange={setFlag("FlagHighlight")}
              />
            }
            label="Highlight as a warning (shown to the technician on My Day)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isOn(modal?.FlagActive)}
                onChange={setFlag("FlagActive")}
              />
            }
            label="Active (can be added to jobs)"
          />
        </div>
      </AppModal>

      <DeleteModal
        show={!!deleteId}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteId(null)}
        customText="Delete this tag? It will drop off every job it is on."
        customButtonText="Yes, Delete It!"
      />
    </div>
  );
};

export default JobTags;
